import { useEffect, useMemo } from "react";
import { useApp } from "../../../context/AppContextProvider";
import { useAuth } from "../../../context/UserContextProvider";
import { useNavigate } from "react-router-dom";
import i18n from "../../../language/i18n";
import Welcome from "../../../components/common/dashboards/Welcome";
import Homework from "../../../components/common/dashboards/Homework";
import LearnerReport from "../../../components/learner/dashboards/Report";
import LearnerAnnouncement from "../../../components/learner/dashboards/Announcement";
import { buildLearnerCalendarEvents } from "../../../utilization/CalendarUtilization";
import { homeScreenStyles } from "./homeScreenStyles";
import { Calendar, Icon } from "../../../library";
import { useTour } from "../../../components/common/tour/tourContext";
import { INTRO_TOUR_SEEN_KEY } from "../../../components/common/tour/TourProvider";

// ── Weekly status / streak card. Day strip + streak count are driven by real logins:
// `activeDaysOfWeek` (0=Mon..6=Sun, days the learner logged in) and
// `currentStreakDays` (consecutive-day login streak) come from the gamification bootstrap. ──
type WeeklyStreak = {
  activeDaysOfWeek: number[];
  currentStreakDays: number;
  todayActive: boolean;
};

const StreakCard = ({
  points,
  achievementCount,
  streak,
}: {
  points: number;
  achievementCount: number;
  streak: WeeklyStreak;
}) => {
  const now = new Date();
  const mondayOffset = (now.getDay() + 6) % 7;
  const activeSet = new Set(streak?.activeDaysOfWeek || []);
  const days = Array.from({ length: 7 }, (_, i) => ({
    // Jan 1 2024 was a Monday → localized Mon..Sun short labels
    label: new Date(2024, 0, 1 + i).toLocaleDateString(i18n.language, {
      weekday: "short",
    }),
    isToday: i === mondayOffset,
    isActive: activeSet.has(i),
  }));
  return (
    <div className="hs-card" data-tour="dash-streak">
      <div className="hs-card__head">
        <div className="hs-card__title">{i18n.t("status")}</div>
      </div>
      <div className="hs-streak__hero">
        <div className="hs-streak__stat">
          <span className="hs-streak__ico">🔥</span>
          <div className="hs-streak__txt">
            <div className="hs-streak__count">
              {streak?.currentStreakDays || 0}
            </div>
            <div className="hs-streak__sub">{i18n.t("day_streak")}</div>
          </div>
        </div>
        <div className="hs-streak__sep" />
        <div className="hs-streak__stat">
          <span className="hs-streak__ico">
            <Icon name="points" size={28} />
          </span>
          <div className="hs-streak__txt">
            <div className="hs-streak__count">{points}</div>
            <div className="hs-streak__sub">{i18n.t("points")}</div>
          </div>
        </div>
        <div className="hs-streak__sep" />
        <div className="hs-streak__stat">
          <span className="hs-streak__ico">🏅</span>
          <div className="hs-streak__txt">
            <div className="hs-streak__count">{achievementCount}</div>
            <div className="hs-streak__sub">{i18n.t("achievements")}</div>
          </div>
        </div>
      </div>
      <div className="hs-streak__week">
        {days.map((d, i) => (
          <div className="hs-streak__day" key={i}>
            <span
              className={
                "hs-streak__dot" +
                (d.isToday ? " is-today" : d.isActive ? " is-done" : "")
              }
            >
              {d.isToday ? (d.isActive ? "🔥" : "") : d.isActive ? "✓" : ""}
            </span>
            <span className={"hs-streak__lbl" + (d.isToday ? " is-today" : "")}>
              {d.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const DashboardScreen = () => {
  const { isFullScreen, setIsFullScreen } = useApp();
  const {
    activeContentList,
    contestDTOList,
    userInformation,
    userProfile,
    userProfileBranch,
    personalStatus,
    weeklyStreak,
    language,
    branchAssignmentListLoading,
    standardExamApplicationListLoading,
    earnedAchievementDTOList,
    mascotInventoryItemDTOList,
    supportAssignmentListLoading,
    announcementDTOList,
  } = useAuth();
  const navigate = useNavigate();
  const { startTour } = useTour();

  // Unified calendar feed: assignment (şube + grup) / exam start+due markers plus
  // announcement "posted" markers, rendered by the shared <Calendar/> primitive.
  // Destek ödevleri arrive inside activeContentList, so there is no second list to pass.
  const calendarEvents = useMemo(
    () =>
      buildLearnerCalendarEvents({
        activeContentList,
        announcementDTOList,
      }),
    [activeContentList, announcementDTOList],
  );

  useEffect(() => {
    if (isFullScreen === true) {
      setIsFullScreen(false);
    }
  }, []);

  // First-run: auto-start the intro tour once (flag persists across logout).
  // Deferred so the dashboard cards + nav are laid out before we anchor to them.
  useEffect(() => {
    let seen = false;
    try {
      seen = localStorage.getItem(INTRO_TOUR_SEEN_KEY) === "true";
    } catch {
      seen = false;
    }
    if (seen) return;
    const timer = setTimeout(() => startTour("intro"), 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ONE actionable list for all four ödev kinds. The row budget is 6, not 3: this card
  // absorbed the deleted Çalışmalar card's space and its three rows, so the group rows it
  // used to show now sit here under the same urgency order (activeContentList is already
  // sorted soonest-deadline-first with no-deadline rows last). The normalized group
  // completionStatus satisfies the same filter — it IS the old `!isSupportAssignmentCompleted`
  // test, expressed in the shared vocabulary.
  const incompleteHomeworks = activeContentList
    .filter(
      (content) =>
        content.completionStatus === "NOT_STARTED" ||
        content.completionStatus === "ON_GOING",
    )
    .slice(0, 6);

  return (
    <>
      <style>{homeScreenStyles}</style>
      <div className="hs" style={{ padding: "1rem" }}>
        <div className="hs-top">
          <Welcome
            name={userInformation.firstName}
            activeContentList={activeContentList}
            contestDTOList={contestDTOList}
            mascotInventoryItemDTOList={mascotInventoryItemDTOList}
            dataTour="dash-welcome"
          />
          <StreakCard
            points={personalStatus.remainingPoints}
            achievementCount={earnedAchievementDTOList.reduce(
              (sum, earned) => sum + (earned.level || 0),
              0,
            )}
            streak={weeklyStreak}
          />
        </div>
        <div className="hs-cols">
          <div className="hs-col">
            <Homework
              contentList={incompleteHomeworks}
              loading={
                branchAssignmentListLoading ||
                supportAssignmentListLoading ||
                standardExamApplicationListLoading
              }
              language={language}
              dataTour="dash-homeworks"
            />
            <LearnerReport dataTour="dash-reports" />
          </div>
          <div className="hs-col">
            <div className="hs-card" data-tour="dash-calendar">
              <Calendar
                events={calendarEvents}
                onEventClick={(e) => e.route && navigate(e.route)}
              />
            </div>
            <LearnerAnnouncement dataTour="dash-announcements" />
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardScreen;
